/**
 * Data client backed by the SELISE Blocks Data Gateway (GraphQL).
 *
 * Replaces the former base44 backend. It exposes the small slice of the old base44
 * client surface the app used (`functions.invoke(name, payload)` and
 * `integrations.Core.UploadFile`), so the existing call sites keep working with a
 * one-line import change.
 *
 * All CRUD goes through a single endpoint: POST {VITE_BLOCKS_API_URL}/data/v4/gateway
 * Auth: `x-blocks-key` header + Bearer token (from the Blocks IAM session in
 * localStorage, via getAuthHeaders). NOTE: the gateway currently enforces a logged-in
 * user for EVERY operation on this deployment (reads included) — an anonymous request
 * returns 401 AUTH_NOT_AUTHENTICATED. So `gql()` refreshes the token and retries once
 * on a 401 before giving up.
 */
import { getAuthHeaders, refreshAccessToken, ensureFreshToken } from './auth';
import { uploadFile as uploadToFileStorage, deleteFileByUrl } from '@/lib/fileStorage';

const BLOCKS_API_URL = (import.meta.env.VITE_BLOCKS_API_URL || 'https://blocksapi.slsblx.com').replace(/\/$/, '');
const GATEWAY_URL = `${BLOCKS_API_URL}/data/v4/gateway`;
const tenantId = import.meta.env.VITE_X_BLOCKS_KEY || '';

// ── low-level GraphQL ───────────────────────────────────────────────────────────

/** True when a gateway response indicates the request was not authenticated. */
function isAuthError(status, payload) {
  if (status === 401) return true;
  return Boolean(
    payload?.errors?.some(
      (e) => e?.extensions?.code === 'AUTH_NOT_AUTHENTICATED' || /not authenticated/i.test(e?.message || ''),
    ),
  );
}

/** Single gateway round-trip. Returns { status, payload }. */
async function gqlRequest(query, variables) {
  const response = await fetch(GATEWAY_URL, {
    method: 'POST',
    // include the Bearer header (from localStorage) AND any Blocks IAM session
    // cookie — the gateway accepts either as proof of authentication.
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-blocks-key': tenantId,
      ...getAuthHeaders(),
    },
    body: JSON.stringify(variables ? { query, variables } : { query }),
  });
  const payload = await response.json().catch(() => null);
  return { status: response.status, ok: response.ok, payload };
}

async function gql(query, variables) {
  // Refresh proactively if the token is expired/near-expiry before spending a round-trip.
  await ensureFreshToken();
  let { status, ok, payload } = await gqlRequest(query, variables);

  // The Bearer token was missing/expired — refresh it once and retry the request.
  if (isAuthError(status, payload)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) ({ status, ok, payload } = await gqlRequest(query, variables));
  }

  if (!ok) {
    const err = new Error(
      payload?.errors?.map((e) => e.message).join('; ') || `Gateway request failed (${status})`,
    );
    err.status = status;
    throw err;
  }
  if (payload?.errors?.length) {
    throw new Error(payload.errors.map((e) => e.message).join('; '));
  }
  return payload?.data ?? {};
}

/** Best-effort decode of the current user's identity for audit `actor`. */
function currentActor() {
  const token = localStorage.getItem('id_token') || localStorage.getItem('access_token');
  if (token) {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const claims = JSON.parse(
        decodeURIComponent(
          atob(padded).split('').map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`).join(''),
        ),
      );
      // Prefer a human-readable identity. Do NOT use the Blocks `sub`
      // ("blocks|<uuid>") — it's opaque; the email/name come from the profile,
      // cached by AuthContext (see below).
      const fromToken = claims.email || claims.name || '';
      if (fromToken) return fromToken;
    } catch {
      // fall through to the cached identity
    }
  }
  // Identity cached by AuthContext from GET /auth/me + /iam/me (email/name). The
  // access token has no email claim, and its JWT is an httpOnly cookie JS can't read
  // — so this cache is what makes audit "changed by" show an email instead of a sub.
  const cached = localStorage.getItem('actor');
  if (cached) return cached;

  // Last resort only: the opaque subject id, if that's all we have.
  return '';
}

// ── field selections / mappers ───────────────────────────────────────────────────

const NODE_FIELDS = `
  ItemId id parent_id node_type name title full_title function_area role_level seniority
  department department_code category photo_url color c_level_id collaborators
  links { type url label } published_at order is_vacancy unit_size CreatedDate LastUpdatedDate
`;

const NODE_INPUT_FIELDS = [
  'id', 'parent_id', 'node_type', 'name', 'title', 'full_title', 'function_area',
  'role_level', 'seniority', 'department', 'department_code', 'category', 'photo_url',
  'color', 'c_level_id', 'collaborators', 'links', 'published_at', 'order', 'is_vacancy',
  'unit_size',
];

/** Map a gateway node to the shape the app expects (system dates → snake_case). */
function mapNode(n) {
  return {
    ...n,
    created_date: n.CreatedDate,
    updated_date: n.LastUpdatedDate,
    collaborators: n.collaborators || [],
    links: (n.links || []).map((l) => ({ type: l.type, url: l.url, label: l.label })),
  };
}

/** Whitelist + coerce a node object into a gateway insert/update input. */
function toNodeInput(node, { includeId } = { includeId: true }) {
  const input = {};
  for (const key of NODE_INPUT_FIELDS) {
    if (key === 'id' && !includeId) continue;
    const v = node[key];
    if (v === undefined || v === null) continue;
    if (key === 'order') {
      const n = Number.parseInt(v, 10);
      if (!Number.isNaN(n)) input.order = n;
    } else if (key === 'is_vacancy') {
      input.is_vacancy = Boolean(v);
    } else if (key === 'collaborators') {
      input.collaborators = Array.isArray(v) ? v : [];
    } else if (key === 'links') {
      input.links = (Array.isArray(v) ? v : []).map((l) => ({
        type: l.type, url: l.url, ...(l.label ? { label: l.label } : {}),
      }));
    } else {
      input[key] = v;
    }
  }
  return input;
}

// ── audit log ─────────────────────────────────────────────────────────────────

async function writeAudit(action, { node_id, node_name, node_type, details }) {
  try {
    await gql(
      `mutation($input: OrgAuditLogInsertInput!){ insertOrgAuditLog(input: $input){ acknowledged } }`,
      { input: { action, node_id, node_name, node_type, details, actor: currentActor() } },
    );
  } catch (err) {
    // Audit failures must never block the primary operation.
    console.warn('[data] audit log write failed:', err.message);
  }
}

// ── operations (mirror the old base44 functions) ─────────────────────────────────

async function getOrgData() {
  const [nodesData, themeData] = await Promise.all([
    gql(`query($paging: PaginationInput){ getOrgNodes(paging: $paging){ items { ${NODE_FIELDS} } totalCount } }`,
      { paging: { pageNo: 1, pageSize: 1000 } }),
    gql(`query{ getThemeConfigs(paging: { pageNo: 1, pageSize: 1 }){ items { ItemId badge_colors } } }`),
  ]);
  const nodes = (nodesData.getOrgNodes?.items || []).map(mapNode);
  const themeConfig = (themeData.getThemeConfigs?.items || [])[0] || null;
  return { status: 200, data: { nodes, themeConfig } };
}

async function manageOrgNode({ action, node, nodeId, reorderItems }) {
  if (action === 'create') {
    const id = node.id || (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.round(Math.random() * 1e9)}`);
    const input = toNodeInput({ ...node, id });
    const data = await gql(
      `mutation($input: OrgNodeInsertInput!){ insertOrgNode(input: $input){ acknowledged itemId } }`,
      { input },
    );
    await writeAudit('create', { node_id: id, node_name: node.name, node_type: node.node_type, details: `Created ${node.name || 'node'}` });
    return { status: 200, data: { id, itemId: data.insertOrgNode?.itemId } };
  }

  if (action === 'update') {
    const input = toNodeInput(node, { includeId: false });
    await gql(
      `mutation($where: OrgNodeFilterInput!, $input: OrgNodeUpdateInput!){ updateOrgNode(where: $where, input: $input){ acknowledged totalImpactedData } }`,
      { where: { id: { eq: node.id } }, input },
    );
    await writeAudit('update', { node_id: node.id, node_name: node.name, node_type: node.node_type, details: `Updated ${node.name || 'node'}` });
    return { status: 200, data: {} };
  }

  if (action === 'delete') {
    // Cascade: delete the node and all its descendants (parent_id chain).
    const { data } = await getOrgData();
    const childrenOf = new Map();
    for (const n of data.nodes) {
      if (!childrenOf.has(n.parent_id)) childrenOf.set(n.parent_id, []);
      childrenOf.get(n.parent_id).push(n);
    }
    const toDelete = [];
    const stack = [nodeId];
    while (stack.length) {
      const id = stack.pop();
      toDelete.push(id);
      for (const child of childrenOf.get(id) || []) stack.push(child.id);
    }
    const target = data.nodes.find((n) => n.id === nodeId);
    for (const id of toDelete) {
      await gql(
        `mutation($where: OrgNodeFilterInput!){ deleteOrgNode(where: $where, input: { isHardDelete: true }){ acknowledged totalImpactedData } }`,
        { where: { id: { eq: id } } },
      );
    }
    await writeAudit('delete', { node_id: nodeId, node_name: target?.name, node_type: target?.node_type, details: `Deleted ${target?.name || 'node'}${toDelete.length > 1 ? ` (+${toDelete.length - 1} descendants)` : ''}` });
    return { status: 200, data: { deleted: toDelete.length } };
  }

  if (action === 'reorder') {
    for (const item of reorderItems || []) {
      await gql(
        `mutation($where: OrgNodeFilterInput!, $input: OrgNodeUpdateInput!){ updateOrgNode(where: $where, input: $input){ acknowledged } }`,
        { where: { id: { eq: item.id } }, input: { order: Number.parseInt(item.order, 10) || 0 } },
      );
    }
    await writeAudit('reorder', { details: `Reordered ${(reorderItems || []).length} items` });
    return { status: 200, data: {} };
  }

  return { status: 400, data: { error: `Unknown manageOrgNode action: ${action}` } };
}

async function publishOrg() {
  const publishedAt = new Date().toISOString();
  await gql(
    `mutation($input: OrgNodeUpdateInput!){ updateManyOrgNode(where: {}, input: $input){ acknowledged totalImpactedData } }`,
    { input: { published_at: publishedAt } },
  );
  return { status: 200, data: { published_at: publishedAt } };
}

async function manageThemeConfig({ badgeColors }) {
  const existing = await gql(`query{ getThemeConfigs(paging: { pageNo: 1, pageSize: 1 }){ items { ItemId } } }`);
  const item = existing.getThemeConfigs?.items?.[0];
  if (item) {
    await gql(
      `mutation($where: ThemeConfigFilterInput!, $input: ThemeConfigUpdateInput!){ updateThemeConfig(where: $where, input: $input){ acknowledged } }`,
      { where: { ItemId: { eq: item.ItemId } }, input: { badge_colors: badgeColors } },
    );
  } else {
    await gql(
      `mutation($input: ThemeConfigInsertInput!){ insertThemeConfig(input: $input){ acknowledged } }`,
      { input: { badge_colors: badgeColors } },
    );
  }
  return { status: 200, data: {} };
}

async function getAuditLog() {
  const data = await gql(
    `query($paging: PaginationInput){ getOrgAuditLogs(paging: $paging){ items { ItemId action node_id node_name node_type details actor CreatedDate } } }`,
    { paging: { pageNo: 1, pageSize: 1000 } },
  );
  const logs = (data.getOrgAuditLogs?.items || [])
    .map((l) => ({ ...l, id: l.ItemId, created_date: l.CreatedDate }))
    .sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
  return { status: 200, data: { logs } };
}

// ── public client (mirrors the base44 surface used across the app) ───────────────

export const dataClient = {
  functions: {
    async invoke(name, payload = {}) {
      switch (name) {
        case 'getOrgData':
          return getOrgData();
        case 'manageOrgNode':
          return manageOrgNode(payload);
        case 'publishOrg':
          return publishOrg();
        case 'manageThemeConfig':
          return manageThemeConfig(payload);
        case 'getAuditLog':
          return getAuditLog();
        case 'inlineImages':
          // No backend image-inlining function; export renders images via their live URLs.
          return { status: 200, data: { images: {} } };
        default:
          console.warn(`[data] unhandled function "${name}"`);
          return { status: 200, data: {} };
      }
    },
  },
  integrations: {
    Core: {
      /**
       * Upload a file to Blocks File Storage and return its public URL.
       * Mirrors the old base44 `UploadFile({ file })` → `{ file_url }` shape used
       * by call sites (e.g. NodeEditModal photo upload).
       */
      async UploadFile({ file }) {
        const { url } = await uploadToFileStorage(file);
        return { file_url: url };
      },
      /**
       * Delete a stored file from Blocks File Storage by its public URL. Best-effort:
       * no-op for empty / non-Blocks URLs (e.g. legacy base44 links).
       */
      async DeleteFile({ url }) {
        await deleteFileByUrl(url);
        return { success: true };
      },
    },
  },
};
